import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Search, Loader, Sparkles, Send, Calendar, RotateCcw, Globe } from 'lucide-react';
import Swal from 'sweetalert2';
import axios from 'axios';
import BASE_URL from '../../url';
import '../styles/ScheduleInterviewModals.css';

// Module-level in-memory cache for open roles to avoid re-fetching on every candidate click
let _cachedOpenRoles = null;
let _cachedOpenRolesTimestamp = 0;
const OPEN_ROLES_TTL_MS = 5 * 60 * 1000; // 5 minutes cache

// Helper to compute match scores for a candidate against active roles
const computeCandidateJobs = (openRoles, candidate) => {
  if (!openRoles || openRoles.length === 0) return [];
  if (!candidate) return openRoles.map(role => ({ ...role, score: 0 }));

  const candidateTitle = (candidate.JobTitle || candidate.jobTitle || candidate.job_title || candidate.title || '').toLowerCase();
  
  const skillsArray = candidate.Skills || candidate.skills || [];
  const candidateSkills = (Array.isArray(skillsArray) ? skillsArray : typeof skillsArray === 'string' ? skillsArray.split(',') : [])
    .map(s => (typeof s === 'string' ? s : s?.SkillName || s?.skill_name || s?.name || '').toLowerCase())
    .filter(Boolean);

  const formattedJobs = openRoles.map(role => {
    const jobTitle = (role.role || '').toLowerCase();
    const jobDesc = (role.jobDescription || '').toLowerCase();
    let score = 0;
    
    // Relevance Analysis
    // 1. Job Title Match
    if (candidateTitle && jobTitle.includes(candidateTitle)) score += 10;
    if (candidateTitle && candidateTitle.includes(jobTitle)) score += 5;
    
    // 2. Skills Match
    candidateSkills.forEach(skill => {
      if (jobTitle.includes(skill)) score += 3;
      if (jobDesc && jobDesc.includes(skill)) score += 2;
    });

    // Format location
    let location = role.location;
    if (!location || location === 'Not specified') {
      const parts = [role.city, role.state, role.country].filter(Boolean);
      location = parts.length > 0 ? parts.join(', ') : 'N/A';
    }

    return {
      id: role.id,
      jobId: role.jobId || role.systemId || `JOB-${role.id}`,
      systemId: role.systemId || `JOB-${String(role.id).padStart(5, '0')}`,
      gbamsId: role.gbamsId || '',
      client: String(role.client || 'N/A'),
      status: String(role.status || 'Active'),
      title: String(role.role || 'N/A'),
      location: String(location || 'N/A'),
      country: String(role.country || ''),
      state: String(role.state || ''),
      city: String(role.city || ''),
      workMode: String(role.roleLocation || 'N/A'),
      roleCategory: String(role.roleCategory || role.roleType || ''),
      regionId: role.regionId || role.RegionId || null,
      roleOwner: String(role.assignTo || 'N/A'),
      assignedRecruiters: String(Array.isArray(role.assignedRecruiters) ? role.assignedRecruiters.map(r => r.name).join(', ') : 'N/A') || 'N/A',
      createdAt: role.createdAt,
      score
    };
  });

  return formattedJobs.sort((a, b) => b.score - a.score);
};

export const SubmitProfileModal = ({ isOpen, onClose, candidates = [], candidate: singleCandidate, onBulkSuccess }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchColumn, setSearchColumn] = useState('all'); // 'all' | 'jobId' | 'title' | 'score' | 'location' | 'workMode' | 'roleOwner' | 'assignedRecruiters' | 'postedDate'
  const [matchFilter, setMatchFilter] = useState('matched'); // 'matched' | 'all'
  const [regionFilter, setRegionFilter] = useState('all'); // 'all' | 'onsite' | 'offshore'
  const [dateQuickSelect, setDateQuickSelect] = useState('all'); // 'all' | 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingJobId, setSubmittingJobId] = useState(null);
  const [hiringStages, setHiringStages] = useState([]);
  const candidate = (candidates && candidates.length > 0) ? candidates[0] : singleCandidate;
  
  let candFirstName = '';
  let candLastName = '';
  let candFullName = 'Candidate';

  if (candidate) {
    candFirstName = candidate.FirstName || candidate.firstName || candidate.first_name || '';
    candLastName = candidate.LastName || candidate.lastName || candidate.last_name || '';
    candFullName = candidate.fullName || candidate.name || `${candFirstName} ${candLastName}`.trim() || 'Candidate';
    
    // Fallback if full name is available but not first/last
    if (candFullName && candFullName !== 'Candidate' && (!candFirstName && !candLastName)) {
      const parts = candFullName.split(' ');
      candFirstName = parts[0];
      candLastName = parts.slice(1).join(' ');
    }
  }

  const candidateId = candidate?.CandidateId || candidate?.id || candidate?.candidate_id || '';

  // Helper date formatting
  const toYMD = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getSearchPlaceholder = (col) => {
    switch (col) {
      case 'location': return 'Search location (e.g. IN, US, Chennai)...';
      case 'jobId': return 'Search Job ID (e.g. 10944676, JOB-02689)...';
      case 'title': return 'Search job title (e.g. Java, Python)...';
      case 'score': return 'Search score (e.g. 65, >=50)...';
      case 'workMode': return 'Search work mode (e.g. Onsite, Remote)...';
      case 'roleOwner': return 'Search role owner name...';
      case 'assignedRecruiters': return 'Search recruiter name...';
      case 'postedDate': return 'Search posted date (e.g. 7/17/2026)...';
      default: return 'Search across all columns...';
    }
  };

  const handleQuickDateChange = (val) => {
    setDateQuickSelect(val);
    const today = new Date();

    if (val === "all") {
      setStartDate("");
      setEndDate("");
      return;
    }

    let start = new Date(today);
    let end = new Date(today);

    if (val === "today") {
      start = new Date(today);
      end = new Date(today);
    } else if (val === "yesterday") {
      start.setDate(today.getDate() - 1);
      end.setDate(today.getDate() - 1);
    } else if (val === "this_week") {
      const dayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday
      const distanceToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      start.setDate(today.getDate() + distanceToMonday);
      end = new Date(today);
    } else if (val === "last_week") {
      const dayOfWeek = today.getDay();
      const distanceToLastMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek) - 7;
      start.setDate(today.getDate() + distanceToLastMonday);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else if (val === "this_month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (val === "last_month") {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (val === "custom") {
      if (!startDate) start.setDate(today.getDate() - 30);
      if (!endDate) end = new Date(today);
    }

    setStartDate(toYMD(start));
    setEndDate(toYMD(end));
  };

  const isJobInRegion = (job, region) => {
    if (region === 'all') return true;

    const ctry = (job.country || '').trim().toLowerCase();
    const loc = (job.location || '').trim().toLowerCase();
    const city = (job.city || '').trim().toLowerCase();
    const state = (job.state || '').trim().toLowerCase();

    // Word-boundary helper: match whole word only (e.g. "india" must not be inside "indianapolis")
    const hasWord = (str, word) => new RegExp(`(^|[^a-z])${word}([^a-z]|$)`).test(str);

    // ── US Detection ──
    const isExplicitUS = 
      ctry === 'us' || ctry === 'usa' || ctry === 'united states' || ctry === 'u.s.' ||
      loc.endsWith(', us') || loc.endsWith(', usa') || loc.endsWith(', united states') ||
      loc.includes(', us') || loc.includes('united states') || loc.endsWith(' us') || loc.endsWith(' usa');

    // ── India Detection (word-boundary safe) ──
    const indiaCountryCodes = ctry === 'india' || ctry === 'ind' || ctry === 'in';
    const indiaCities = 
      city === 'chennai' || city === 'bangalore' || city === 'bengaluru' ||
      city === 'hyderabad' || city === 'pune' || city === 'noida' ||
      city === 'gurgaon' || city === 'gurugram' || city === 'delhi' ||
      city === 'mumbai' || city === 'kolkata' || city === 'coimbatore' || city === 'kochi' ||
      city === 'ahmedabad' || city === 'jaipur' || city === 'bhopal' || city === 'lucknow';

    const isLocIn = !isExplicitUS && (
      loc === 'in' || 
      loc.endsWith(', in') || 
      loc.includes(', in,') || 
      loc.includes(', ts, in') || 
      loc.includes(', mh, in') || 
      loc.includes(', ka, in') || 
      loc.includes(', tn, in') ||
      loc.startsWith('in,') ||
      loc.startsWith('in -')
    );

    const indiaInLocation = 
      hasWord(loc, 'india') || hasWord(loc, 'chennai') ||
      hasWord(loc, 'bangalore') || hasWord(loc, 'bengaluru') ||
      hasWord(loc, 'hyderabad') || hasWord(loc, 'pune') ||
      hasWord(loc, 'noida') || hasWord(loc, 'gurgaon') || hasWord(loc, 'gurugram') ||
      hasWord(loc, 'delhi') || hasWord(loc, 'mumbai') ||
      hasWord(loc, 'kolkata') || hasWord(loc, 'coimbatore') || hasWord(loc, 'kochi') ||
      // Indian state names / abbreviations only when NOT followed by ", US"
      (hasWord(loc, 'telangana') && !isExplicitUS) ||
      (hasWord(loc, 'karnataka') && !isExplicitUS) ||
      (hasWord(loc, 'maharashtra') && !isExplicitUS) ||
      (hasWord(loc, 'tamil nadu') && !isExplicitUS) ||
      isLocIn ||
      (!isExplicitUS && indiaCountryCodes);

    const isExplicitIndia = indiaCountryCodes || indiaCities || indiaInLocation || isLocIn;

    const roleCat = (job.roleCategory || '').toLowerCase().trim();
    const isRoleOffshore = Number(job.regionId) === 2 || roleCat === 'offshore';
    const isOffshoreMatch = isRoleOffshore || (isExplicitIndia && !isExplicitUS);

    if (region === 'offshore') {
      // Must match India / Offshore AND not be a US job
      if (isExplicitUS && !isRoleOffshore) return false;
      return isOffshoreMatch;
    }

    if (region === 'onsite') {
      // Must NOT be an India / Offshore job
      if (isOffshoreMatch && !isExplicitUS) return false;
      return true;
    }

    return true;
  };

  const isJobInDateRange = (job, startStr, endStr) => {
    if (!startStr && !endStr) return true;
    if (!job.createdAt) return true;

    const jobDate = new Date(job.createdAt);
    if (isNaN(jobDate.getTime())) return true;

    const jobYMD = toYMD(jobDate);

    if (startStr && endStr) {
      return jobYMD >= startStr && jobYMD <= endStr;
    } else if (startStr) {
      return jobYMD >= startStr;
    } else if (endStr) {
      return jobYMD <= endStr;
    }
    return true;
  };

  const fetchJobs = useCallback(async (force = false) => {
    const isCacheValid = _cachedOpenRoles && (Date.now() - _cachedOpenRolesTimestamp < OPEN_ROLES_TTL_MS);

    // If cached and valid, compute matching immediately with 0ms loading time
    if (isCacheValid && !force) {
      const allSortedJobs = computeCandidateJobs(_cachedOpenRoles, candidate);
      setJobs(allSortedJobs);
      setLoading(false);
      return;
    }

    // If cache exists but expired or forcing, show cached immediately while fetching fresh data in background
    if (_cachedOpenRoles && _cachedOpenRoles.length > 0) {
      const allSortedJobs = computeCandidateJobs(_cachedOpenRoles, candidate);
      setJobs(allSortedJobs);
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem('token');
      // Request open roles (Active, Bench, Modified)
      const res = await axios.get(`${BASE_URL}/api/recruitment/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out closed, cancelled, or inactive roles
      const openRoles = (res.data || []).filter(role => {
        const st = (role.status || '').toLowerCase().trim();
        return st !== 'cancelled' && st !== 'inactive' && st !== 'draft' && st !== 'filled';
      });

      _cachedOpenRoles = openRoles;
      _cachedOpenRolesTimestamp = Date.now();

      const allSortedJobs = computeCandidateJobs(openRoles, candidate);
      setJobs(allSortedJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      if (!_cachedOpenRoles) {
        Swal.fire('Error', 'Failed to fetch jobs.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [candidate]);

  useEffect(() => {
    if (isOpen && candidate) {
      fetchJobs(false);
    }
  }, [isOpen, candidateId, fetchJobs]);

  useEffect(() => {
    const fetchStages = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/recruitment/applications/hiring-steps`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const progressive = (res.data || []).filter(s => {
          const code = (s.stageCode || '').toUpperCase();
          const name = (s.stepName || '').toLowerCase().trim();
          return !['REJECTED', 'WITHDRAWN', 'REJECTED_INTERNAL', 'WITHDRAWN_INTERNAL', 'HOLD', 'ON_HOLD', 'HOLD_INTERNAL'].includes(code) &&
                 !name.includes('rejected') && !name.includes('withdrawn') && !name.includes('hold');
        });
        setHiringStages(progressive);
      } catch (err) {
        console.error('Failed to fetch hiring stages:', err);
      }
    };
    fetchStages();
  }, []);

  const handleSubmitProfile = async (job) => {
    const targetCandidates = (candidates && candidates.length > 0) ? candidates : (singleCandidate ? [singleCandidate] : []);
    const isBulk = targetCandidates.length > 1;
    const confirmText = isBulk
      ? `You are submitting <b>${targetCandidates.length} candidates</b> for the role <b>"${job.title}"</b>`
      : `You are submitting <b>${candFullName}</b> for the role <b>"${job.title}"</b>`;

    let htmlContent = '';
    
    if (isBulk) {
      // Build master options for the "Set All" dropdown
      let allOptionsHtml = '<option value="" disabled selected>-- Choose to apply to all --</option>';
      hiringStages.forEach((stage, idx) => {
        allOptionsHtml += `<option value='${JSON.stringify({
          stageCode: stage.stageCode,
          phaseCode: stage.phaseCode,
          stepOrder: stage.stepOrder,
          stepIndex: idx,
          stepName: stage.stepName
        })}'>${stage.stepName}</option>`;
      });

      // Master "Set All" toolbar
      htmlContent += `
        <div style="font-size: 13.5px; color: #475569; margin-bottom: 12px; line-height: 1.4;">${confirmText}</div>
        <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 12px 14px; margin-bottom: 12px; text-align: left;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 8px;">
            <span style="font-size: 12px; font-weight: 700; color: #2563eb; display: flex; align-items: center; gap: 6px;">
              ⚡ Quick Action: Set All Candidates To:
            </span>
            <select id="stage-select-all" style="padding: 6px 10px; font-size: 12px; border-radius: 6px; border: 1px solid #3b5bfd; background: #ffffff; color: #2563eb; font-weight: 600; cursor: pointer; outline: none;">
              ${allOptionsHtml}
            </select>
          </div>
          <div>
            <input 
              type="text" 
              id="stage-note-all" 
              placeholder="📝 Quick Note for all candidates (e.g., Screened by recruiter, submitted for review)..." 
              style="width: 100%; padding: 6px 10px; font-size: 12px; border-radius: 6px; border: 1px solid #bfdbfe; background: #ffffff; color: #2563eb; box-sizing: border-box; outline: none;" 
            />
          </div>
        </div>
        <div style="text-align: left; margin-bottom: 6px; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">
          Individual Pipeline Stages & Notes (${targetCandidates.length} candidates):
        </div>
        <div style="max-height: 300px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; background: #ffffff; padding: 6px;">
      `;

      targetCandidates.forEach((c, i) => {
        const cF = c.FirstName || c.firstName || c.first_name || '';
        const cL = c.LastName || c.lastName || c.last_name || '';
        const cName = `${cF} ${cL}`.trim() || c.fullName || c.name || `Candidate #${i + 1}`;
        const cMail = c.email || c.Email || c.Contact?.Email || c.contact_email || '';

        // Generate options with default to Resume Review or first stage
        let candOptionsHtml = '<option value="" disabled>-- Select Pipeline Stage --</option>';
        hiringStages.forEach((stage, idx) => {
          const isDefault = stage.stageCode === 'RESUME_REVIEW' || stage.stepName?.toLowerCase().includes('resume review') || idx === 1 || (idx === 0 && hiringStages.length === 1);
          candOptionsHtml += `<option value='${JSON.stringify({
            stageCode: stage.stageCode,
            phaseCode: stage.phaseCode,
            stepOrder: stage.stepOrder,
            stepIndex: idx,
            stepName: stage.stepName
          })}' ${isDefault ? 'selected' : ''}>${stage.stepName}</option>`;
        });

        htmlContent += `
          <div style="padding: 8px 10px; border-bottom: ${i === targetCandidates.length - 1 ? 'none' : '1px solid #f1f5f9'}; background: ${i % 2 === 0 ? '#f8fafc' : '#ffffff'}; border-radius: 6px; margin-bottom: 4px;">
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 6px;">
              <div style="text-align: left; min-width: 140px; max-width: 230px; overflow: hidden;">
                <div style="font-size: 12.5px; font-weight: 700; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                  ${i + 1}. ${cName}
                </div>
                ${cMail ? `<div style="font-size: 10.5px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${cMail}</div>` : ''}
              </div>
              <div style="flex-shrink: 0;">
                <select id="stage-select-cand-${i}" class="cand-stage-dropdown" style="padding: 5px 8px; font-size: 11.5px; font-weight: 600; border-radius: 6px; border: 1px solid #cbd5e1; background: #ffffff; color: #1e293b; cursor: pointer; outline: none;">
                  ${candOptionsHtml}
                </select>
              </div>
            </div>
            <div>
              <input 
                type="text" 
                id="stage-note-cand-${i}" 
                class="cand-note-input" 
                placeholder="Candidate note / remarks (optional)..." 
                style="width: 100%; padding: 5px 8px; font-size: 11.5px; border-radius: 4px; border: 1px solid #cbd5e1; background: #ffffff; color: #334155; box-sizing: border-box; outline: none;" 
              />
            </div>
          </div>
        `;
      });

      htmlContent += '</div>';

    } else {
      // Single Candidate Mode: Interactive Stage & Outcome Picker
      const defaultIdx = hiringStages.findIndex((s, idx) =>
        s.stageCode === 'RESUME_REVIEW' || s.stepName?.toLowerCase().includes('resume review') || idx === 1
      );
      const initialSelectedIdx = defaultIdx >= 0 ? defaultIdx : 0;
      const initialStage = hiringStages[initialSelectedIdx] || {};

      let stagesListHtml = '';
      hiringStages.forEach((stage, idx) => {
        const isDefault = idx === initialSelectedIdx;
        stagesListHtml += `
          <div 
            class="swal-stage-row" 
            id="swal-stage-row-${idx}"
            data-idx="${idx}" 
            style="display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; margin-bottom: 5px; border-radius: 7px; border: 1.5px solid ${isDefault ? '#3b5bfd' : '#e2e8f0'}; background: ${isDefault ? '#eff6ff' : '#ffffff'}; cursor: pointer; transition: all 0.15s ease;"
          >
            <div class="swal-stage-select-target" data-idx="${idx}" style="display: flex; align-items: center; gap: 9px; flex: 1; text-align: left;">
              <input 
                type="radio" 
                name="swal_selected_stage_radio" 
                id="swal-stage-radio-${idx}" 
                value="${idx}" 
                ${isDefault ? 'checked' : ''} 
                style="accent-color: #3b5bfd; width: 16px; height: 16px; cursor: pointer;"
              />
              <div>
                <div style="font-size: 13px; font-weight: 700; color: #1e293b;">
                  ${idx + 1}. ${stage.stepName}
                </div>
                ${stage.phaseCode ? `<div style="font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase;">${stage.phaseCode} PHASE</div>` : ''}
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 5px; flex-shrink: 0;">
              <button 
                type="button" 
                class="swal-stage-action-btn swal-stage-reject-btn" 
                id="swal-reject-btn-${idx}"
                data-idx="${idx}" 
                title="Reject candidate at ${stage.stepName}" 
                style="display: inline-flex; align-items: center; gap: 2px; padding: 4px 7px; font-size: 11px; font-weight: 700; border-radius: 5px; border: 1px solid #fca5a5; background: #ffffff; color: #dc2626; cursor: pointer; transition: all 0.15s ease;"
              >
                🔴 Reject
              </button>
              <button 
                type="button" 
                class="swal-stage-action-btn swal-stage-hold-btn" 
                id="swal-hold-btn-${idx}"
                data-idx="${idx}" 
                title="Hold candidate at ${stage.stepName}" 
                style="display: inline-flex; align-items: center; gap: 2px; padding: 4px 7px; font-size: 11px; font-weight: 700; border-radius: 5px; border: 1px solid #c4b5fd; background: #ffffff; color: #7c3aed; cursor: pointer; transition: all 0.15s ease;"
              >
                ⏸ Hold
              </button>
              <button 
                type="button" 
                class="swal-stage-action-btn swal-stage-withdraw-btn" 
                id="swal-withdraw-btn-${idx}"
                data-idx="${idx}" 
                title="Withdraw candidate at ${stage.stepName}" 
                style="display: inline-flex; align-items: center; gap: 2px; padding: 4px 7px; font-size: 11px; font-weight: 700; border-radius: 5px; border: 1px solid #fcd34d; background: #ffffff; color: #d97706; cursor: pointer; transition: all 0.15s ease;"
              >
                🟠 Withdraw
              </button>
            </div>
          </div>
        `;
      });

      htmlContent = `
        <div style="font-size: 13.5px; color: #334155; line-height: 1.5; margin-bottom: 12px; text-align: left;">${confirmText}</div>

        <!-- Action Status Feedback Banner -->
        <div 
          id="single-action-banner" 
          style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 10px 14px; margin-bottom: 12px; display: flex; align-items: center; justify-content: space-between; gap: 10px; text-align: left;"
        >
          <div id="single-action-text" style="font-size: 13px; font-weight: 700; color: #2563eb;">
            🟢 Action: Move to <strong>${initialStage.stepName || 'Resume Review'}</strong> (Active)
          </div>
          <span 
            id="single-action-badge" 
            style="font-size: 11px; font-weight: 800; padding: 3px 8px; border-radius: 12px; background: #dbeafe; color: #2563eb; text-transform: uppercase; letter-spacing: 0.5px; flex-shrink: 0;"
          >
            ACTIVE
          </span>
        </div>

        <div style="text-align: left; margin-bottom: 6px;">
          <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px;">
            Pipeline Stage & Action (Click step to advance, or click 🔴/⏸/🟠 to reject/hold/withdraw) <span style="color: #ef4444;">*</span>
          </label>
        </div>

        <!-- Scrollable Stage List with per-step icons -->
        <div 
          id="single-stage-list-container" 
          style="max-height: 250px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; padding: 6px; margin-bottom: 12px;"
        >
          ${stagesListHtml}
        </div>

        <div style="text-align: left; margin-top: 10px;">
          <label id="single-notes-label" style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px;">
            Stage Notes / Remarks (Optional)
          </label>
          <textarea 
            id="single-candidate-notes" 
            placeholder="Add candidate evaluation, remarks, or notes for this stage..." 
            rows="3" 
            style="width: 100%; padding: 8px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13px; font-family: inherit; color: #0f172a; box-sizing: border-box; outline: none; resize: vertical; line-height: 1.45;"
          ></textarea>
        </div>

        <div id="single-entity-container" style="display: none; margin-top: 12px; background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0; text-align: left;">
          <label style="font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; display: block;">
            Select Onboarding Entity / Company *
          </label>
          <select id="single-onboarding-entity" style="width: 100%; padding: 8px 10px; border-radius: 6px; border: 1px solid #cbd5e1; font-size: 13.5px; font-weight: 500; color: #0f172a; background: #ffffff; outline: none;">
            <option value="4" selected>Prophecy Consulting INC (US Entity)</option>
            <option value="5">Prophecy Offshore (India Entity)</option>
            <option value="6">Cognifyar Technologies</option>
          </select>
        </div>
      `;
    }

    const defaultIdx = hiringStages.findIndex((s, idx) =>
      s.stageCode === 'RESUME_REVIEW' || s.stepName?.toLowerCase().includes('resume review') || idx === 1
    );
    let selectedStepIndex = defaultIdx >= 0 ? defaultIdx : 0;
    let selectedOutcomeType = 'Active'; // 'Active' | 'Rejected' | 'Withdrawn' | 'Hold'

    const confirm = await Swal.fire({
      title: isBulk ? 'Submit Bulk Resumes?' : 'Submit Profile?',
      html: htmlContent,
      width: isBulk ? '640px' : '560px',
      showCancelButton: true,
      confirmButtonText: 'Yes, Submit',
      confirmButtonColor: '#3b5bfd',
      didOpen: () => {
        if (isBulk) {
          const setAllSelect = document.getElementById('stage-select-all');
          if (setAllSelect) {
            setAllSelect.addEventListener('change', (e) => {
              const val = e.target.value;
              if (!val) return;
              for (let i = 0; i < targetCandidates.length; i++) {
                const candSelect = document.getElementById(`stage-select-cand-${i}`);
                if (candSelect) {
                  candSelect.value = val;
                }
              }
            });
          }

          const setAllNote = document.getElementById('stage-note-all');
          if (setAllNote) {
            setAllNote.addEventListener('input', (e) => {
              const noteVal = e.target.value;
              for (let i = 0; i < targetCandidates.length; i++) {
                const candNote = document.getElementById(`stage-note-cand-${i}`);
                if (candNote) {
                  candNote.value = noteVal;
                }
              }
            });
          }
        } else {
          const bannerEl = document.getElementById('single-action-banner');
          const bannerTextEl = document.getElementById('single-action-text');
          const bannerBadgeEl = document.getElementById('single-action-badge');
          const notesLabelEl = document.getElementById('single-notes-label');
          const notesEl = document.getElementById('single-candidate-notes');
          const entityContainer = document.getElementById('single-entity-container');
          const confirmBtn = Swal.getConfirmButton();

          const updateSingleState = (idx, outcomeType) => {
            selectedStepIndex = idx;
            selectedOutcomeType = outcomeType;
            const currentStage = hiringStages[idx] || {};
            const stageName = currentStage.stepName || `Stage ${idx + 1}`;

            // 1. Update rows and buttons
            hiringStages.forEach((s, sIdx) => {
              const row = document.getElementById(`swal-stage-row-${sIdx}`);
              const radio = document.getElementById(`swal-stage-radio-${sIdx}`);
              const rejBtn = document.getElementById(`swal-reject-btn-${sIdx}`);
              const holdBtn = document.getElementById(`swal-hold-btn-${sIdx}`);
              const wdrBtn = document.getElementById(`swal-withdraw-btn-${sIdx}`);

              if (sIdx === idx) {
                if (radio) radio.checked = true;
                if (outcomeType === 'Rejected') {
                  if (row) { row.style.background = '#fef2f2'; row.style.border = '1.5px solid #ef4444'; }
                  if (rejBtn) { rejBtn.style.background = '#dc2626'; rejBtn.style.color = '#ffffff'; rejBtn.style.border = '1px solid #dc2626'; }
                  if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                  if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
                } else if (outcomeType === 'Hold') {
                  if (row) { row.style.background = '#f5f3ff'; row.style.border = '1.5px solid #7c3aed'; }
                  if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                  if (holdBtn) { holdBtn.style.background = '#7c3aed'; holdBtn.style.color = '#ffffff'; holdBtn.style.border = '1px solid #7c3aed'; }
                  if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
                } else if (outcomeType === 'Withdrawn') {
                  if (row) { row.style.background = '#fffbeb'; row.style.border = '1.5px solid #f59e0b'; }
                  if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                  if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                  if (wdrBtn) { wdrBtn.style.background = '#d97706'; wdrBtn.style.color = '#ffffff'; wdrBtn.style.border = '1px solid #d97706'; }
                } else {
                  if (row) { row.style.background = '#eff6ff'; row.style.border = '1.5px solid #3b5bfd'; }
                  if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                  if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                  if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
                }
              } else {
                if (radio) radio.checked = false;
                if (row) { row.style.background = '#ffffff'; row.style.border = '1px solid #e2e8f0'; }
                if (rejBtn) { rejBtn.style.background = '#ffffff'; rejBtn.style.color = '#dc2626'; rejBtn.style.border = '1px solid #fca5a5'; }
                if (holdBtn) { holdBtn.style.background = '#ffffff'; holdBtn.style.color = '#7c3aed'; holdBtn.style.border = '1px solid #c4b5fd'; }
                if (wdrBtn) { wdrBtn.style.background = '#ffffff'; wdrBtn.style.color = '#d97706'; wdrBtn.style.border = '1px solid #fcd34d'; }
              }
            });

            // 2. Update Banner and Confirm Button
            if (outcomeType === 'Rejected') {
              if (bannerEl) { bannerEl.style.background = '#fef2f2'; bannerEl.style.border = '1px solid #fca5a5'; }
              if (bannerTextEl) { bannerTextEl.innerHTML = `🔴 Action: REJECT candidate at <strong>${stageName}</strong>`; bannerTextEl.style.color = '#b91c1c'; }
              if (bannerBadgeEl) { bannerBadgeEl.innerText = 'REJECTED'; bannerBadgeEl.style.background = '#fee2e2'; bannerBadgeEl.style.color = '#dc2626'; }
              if (notesLabelEl) { notesLabelEl.innerHTML = `Rejection Reason (Required) <span style="color: #ef4444;">*</span>`; }
              if (notesEl) { notesEl.placeholder = `Please provide the reason for rejection at ${stageName}...`; notesEl.style.borderColor = '#fca5a5'; notesEl.focus(); }
              if (confirmBtn) { confirmBtn.innerText = 'Confirm Rejection'; confirmBtn.style.backgroundColor = '#ef4444'; }
              if (entityContainer) entityContainer.style.display = 'none';
            } else if (outcomeType === 'Hold') {
              if (bannerEl) { bannerEl.style.background = '#f5f3ff'; bannerEl.style.border = '1px solid #c4b5fd'; }
              if (bannerTextEl) { bannerTextEl.innerHTML = `⏸ Action: Place candidate on HOLD at <strong>${stageName}</strong>`; bannerTextEl.style.color = '#6d28d9'; }
              if (bannerBadgeEl) { bannerBadgeEl.innerText = 'ON HOLD'; bannerBadgeEl.style.background = '#ede9fe'; bannerBadgeEl.style.color = '#7c3aed'; }
              if (notesLabelEl) { notesLabelEl.innerHTML = `Hold Reason (Required) <span style="color: #7c3aed;">*</span>`; }
              if (notesEl) { notesEl.placeholder = `Please provide the reason for placing candidate on hold at ${stageName}...`; notesEl.style.borderColor = '#c4b5fd'; notesEl.focus(); }
              if (confirmBtn) { confirmBtn.innerText = 'Confirm Hold'; confirmBtn.style.backgroundColor = '#7c3aed'; }
              if (entityContainer) entityContainer.style.display = 'none';
            } else if (outcomeType === 'Withdrawn') {
              if (bannerEl) { bannerEl.style.background = '#fffbeb'; bannerEl.style.border = '1px solid #fcd34d'; }
              if (bannerTextEl) { bannerTextEl.innerHTML = `🟠 Action: WITHDRAW candidate at <strong>${stageName}</strong>`; bannerTextEl.style.color = '#b45309'; }
              if (bannerBadgeEl) { bannerBadgeEl.innerText = 'WITHDRAWN'; bannerBadgeEl.style.background = '#fef3c7'; bannerBadgeEl.style.color = '#d97706'; }
              if (notesLabelEl) { notesLabelEl.innerHTML = `Withdrawal Reason (Required) <span style="color: #d97706;">*</span>`; }
              if (notesEl) { notesEl.placeholder = `Please provide the reason for withdrawal at ${stageName}...`; notesEl.style.borderColor = '#fcd34d'; notesEl.focus(); }
              if (confirmBtn) { confirmBtn.innerText = 'Confirm Withdrawal'; confirmBtn.style.backgroundColor = '#d97706'; }
              if (entityContainer) entityContainer.style.display = 'none';
            } else {
              if (bannerEl) { bannerEl.style.background = '#eff6ff'; bannerEl.style.border = '1px solid #bfdbfe'; }
              if (bannerTextEl) { bannerTextEl.innerHTML = `🟢 Action: Move to <strong>${stageName}</strong> (Active)`; bannerTextEl.style.color = '#2563eb'; }
              if (bannerBadgeEl) { bannerBadgeEl.innerText = 'ACTIVE'; bannerBadgeEl.style.background = '#dbeafe'; bannerBadgeEl.style.color = '#2563eb'; }
              if (notesLabelEl) { notesLabelEl.innerHTML = `Stage Notes / Remarks (Optional)`; }
              if (notesEl) { notesEl.placeholder = `Add candidate evaluation, remarks, or notes for this stage...`; notesEl.style.borderColor = '#cbd5e1'; }
              if (confirmBtn) { confirmBtn.innerText = 'Yes, Submit'; confirmBtn.style.backgroundColor = '#3b5bfd'; }
              
              const isOnboarding = (currentStage.stageCode || '').toUpperCase() === 'ONBOARDING_INITIATED' || 
                                   (currentStage.stepName || '').toLowerCase().includes('onboarding initiate');
              if (entityContainer) entityContainer.style.display = isOnboarding ? 'block' : 'none';
            }
          };

          // Attach click handlers to each row and reject/hold/withdraw buttons
          hiringStages.forEach((s, idx) => {
            const row = document.getElementById(`swal-stage-row-${idx}`);
            const selectTarget = row ? row.querySelector('.swal-stage-select-target') : null;
            const radio = document.getElementById(`swal-stage-radio-${idx}`);
            const rejBtn = document.getElementById(`swal-reject-btn-${idx}`);
            const holdBtn = document.getElementById(`swal-hold-btn-${idx}`);
            const wdrBtn = document.getElementById(`swal-withdraw-btn-${idx}`);

            const selectActive = () => updateSingleState(idx, 'Active');
            if (selectTarget) selectTarget.addEventListener('click', selectActive);
            if (radio) radio.addEventListener('click', selectActive);

            if (rejBtn) {
              rejBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                updateSingleState(idx, 'Rejected');
              });
            }

            if (holdBtn) {
              holdBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                updateSingleState(idx, 'Hold');
              });
            }

            if (wdrBtn) {
              wdrBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                updateSingleState(idx, 'Withdrawn');
              });
            }
          });

          // Check onboarding initial state
          const initialStageObj = hiringStages[selectedStepIndex] || {};
          const isInitialOnboarding = (initialStageObj.stageCode || '').toUpperCase() === 'ONBOARDING_INITIATED' || 
                                      (initialStageObj.stepName || '').toLowerCase().includes('onboarding initiate');
          if (entityContainer) entityContainer.style.display = isInitialOnboarding ? 'block' : 'none';
        }
      },
      preConfirm: () => {
        if (isBulk) {
          const selectedStages = [];
          for (let i = 0; i < targetCandidates.length; i++) {
            const select = document.getElementById(`stage-select-cand-${i}`);
            const noteInput = document.getElementById(`stage-note-cand-${i}`);
            if (!select || !select.value) {
              const c = targetCandidates[i];
              const cName = `${c.FirstName || c.firstName || ''} ${c.LastName || c.lastName || ''}`.trim() || `Candidate #${i + 1}`;
              Swal.showValidationMessage(`Please select a pipeline stage for ${cName}`);
              return false;
            }
            try {
              const parsed = JSON.parse(select.value);
              selectedStages.push({
                ...parsed,
                notes: noteInput ? noteInput.value.trim() : ''
              });
            } catch (e) {
              Swal.showValidationMessage(`Invalid pipeline stage for Candidate #${i + 1}`);
              return false;
            }
          }
          return selectedStages;
        } else {
          const notesEl = document.getElementById('single-candidate-notes');
          const entityEl = document.getElementById('single-onboarding-entity');
          const stageObj = hiringStages[selectedStepIndex];
          if (!stageObj) {
            Swal.showValidationMessage('Please select a pipeline stage');
            return false;
          }

          const notesVal = notesEl ? notesEl.value.trim() : '';
          if ((selectedOutcomeType === 'Rejected' || selectedOutcomeType === 'Withdrawn' || selectedOutcomeType === 'Hold') && !notesVal) {
            Swal.showValidationMessage(`Please enter a reason for ${selectedOutcomeType.toLowerCase()} at ${stageObj.stepName}`);
            return false;
          }

          const isOnboarding = (stageObj.stageCode || '').toUpperCase() === 'ONBOARDING_INITIATED' || 
                               (stageObj.stepName || '').toLowerCase().includes('onboarding initiate');
          return [{
            stageCode: stageObj.stageCode,
            phaseCode: selectedOutcomeType !== 'Active' ? 'OUTCOME' : stageObj.phaseCode,
            stepOrder: stageObj.stepOrder,
            stepIndex: selectedStepIndex,
            stepName: stageObj.stepName,
            outcomeType: selectedOutcomeType,
            status: selectedOutcomeType !== 'Active' ? selectedOutcomeType : 'Active',
            rejectedAtStage: selectedOutcomeType !== 'Active' ? stageObj.stepName : null,
            fromStageCode: stageObj.stageCode,
            notes: notesVal,
            entityId: (isOnboarding && entityEl && selectedOutcomeType === 'Active') ? parseInt(entityEl.value) : undefined
          }];
        }
      }
    });

    if (!confirm.isConfirmed) return;
    const selectedStagesList = confirm.value || [];

    setSubmittingJobId(job.id);
    try {
      const token = localStorage.getItem('token');
      
      for (let i = 0; i < targetCandidates.length; i++) {
        const currentCandidate = targetCandidates[i];
        const selectedStage = selectedStagesList[i] || selectedStagesList[0];
        
        const cFirstName = currentCandidate.FirstName || currentCandidate.firstName || currentCandidate.first_name || '';
        const cLastName = currentCandidate.LastName || currentCandidate.lastName || currentCandidate.last_name || '';
        const cFullName = `${cFirstName} ${cLastName}`.trim() || 'Candidate';
        const cEmail = currentCandidate.email || currentCandidate.Email || currentCandidate.Contact?.Email || currentCandidate.contact_email || currentCandidate.ContactEmail || '';
        const cPhone = currentCandidate.phone || currentCandidate.Phone || currentCandidate.Contact?.Phone || currentCandidate.Mobile || currentCandidate.mobile || '';
        const cLocation = currentCandidate.location || currentCandidate.Location || currentCandidate.CurrentLocation || currentCandidate.current_location || '';
        const cExp = currentCandidate.experience || currentCandidate.YearsOfExperience || currentCandidate.years_of_experience || currentCandidate.total_experience || '';

        if (isBulk) {
          Swal.fire({
            title: 'Submitting Profiles...',
            text: `Submitting candidate ${i + 1} of ${targetCandidates.length}: ${cFullName} (${selectedStage?.stepName || 'New'})`,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });
        }

        const formData = new FormData();
        if (selectedStage) {
            const isOutcome = selectedStage.outcomeType === 'Rejected' || selectedStage.outcomeType === 'Withdrawn' || selectedStage.outcomeType === 'Hold';
            if (isOutcome) {
                formData.append('status', selectedStage.outcomeType);
                formData.append('rejectedAtStage', selectedStage.stepName || '');
                formData.append('fromStageCode', selectedStage.stageCode || 'NEW');
                const outcomeStageCode = selectedStage.outcomeType === 'Rejected' ? 'REJECTED' : (selectedStage.outcomeType === 'Hold' ? 'HOLD' : 'WITHDRAWN');
                formData.append('currentStageCode', outcomeStageCode);
                formData.append('currentPhaseCode', 'OUTCOME');
                formData.append('currentStep', selectedStage.stepIndex !== undefined ? selectedStage.stepIndex : (selectedStage.stepOrder || 0));
            } else {
                formData.append('currentStageCode', selectedStage.stageCode || 'NEW');
                formData.append('currentPhaseCode', selectedStage.phaseCode || 'INTERNAL');
                formData.append('currentStep', selectedStage.stepIndex !== undefined ? selectedStage.stepIndex : (selectedStage.stepOrder || 0));
            }
            if (selectedStage.notes) {
                formData.append('notes', selectedStage.notes);
            }
            if (selectedStage.entityId) {
                formData.append('entityId', selectedStage.entityId);
            }
        }
        formData.append('roleId', job.id);
        
        formData.append('candidateFirstName', cFirstName);
        formData.append('candidateLastName', cLastName);
        formData.append('name', cFullName);
        formData.append('email', cEmail);
        formData.append('phone', cPhone);
        formData.append('experience', cExp);
        formData.append('location', cLocation);
        formData.append('currentLocation', cLocation);
        formData.append('linkedInUrl', currentCandidate.linkedInUrl || currentCandidate.LinkedInUrl || '');
        
        const rawSkills = currentCandidate.Skills || currentCandidate.skills || [];
        const skillsText = Array.isArray(rawSkills) 
          ? rawSkills.map(s => typeof s === 'string' ? s : (s.SkillName || s.skill_name || s.name || '')).filter(Boolean).join(', ')
          : String(rawSkills);
        formData.append('skills', skillsText);
        
        formData.append('workAuthorization', currentCandidate.workAuthorization || currentCandidate.WorkAuthorization || currentCandidate.work_authorization || '');
        formData.append('currentCompany', (currentCandidate.WorkExperience || currentCandidate.workExperience || [])[0]?.Company || currentCandidate.CurrentCompany || currentCandidate.currentCompany || currentCandidate.current_company || '');
        formData.append('currentEmployerAddress', currentCandidate.CurrentEmployerAddress || '');
        formData.append('expectedSalary', currentCandidate.expectedSalary || currentCandidate.ExpectedRateFrom || currentCandidate.expected_rate_from || '');
        formData.append('rate', currentCandidate.rate || currentCandidate.CurrentRate || currentCandidate.current_rate || '');
        formData.append('highestDegree', (currentCandidate.Education || currentCandidate.education || [])[0]?.Degree || currentCandidate.HighestDegree || currentCandidate.highest_degree || '');
        formData.append('currentEmployer', currentCandidate.currentEmployer || currentCandidate.CurrentEmployer || currentCandidate.current_employer || '');
        formData.append('availability', currentCandidate.Availability || '');
        formData.append('noticePeriod', currentCandidate.noticePeriod || currentCandidate.NoticePeriod || currentCandidate.notice_period || ''); 
        
        formData.append('totalITExperience', cExp);
        formData.append('relevantExperience', currentCandidate.RelevantExperience || currentCandidate.relevantExperience || '');
        formData.append('experience', currentCandidate.YearsOfExperience || '');
        
        const candidateId = currentCandidate.CandidateId || currentCandidate.candidate_id;
        if (candidateId) {
          try {
            let documentId = null;
            
            try {
              const aiRes = await axios.post(`${BASE_URL}/api/recruitment/applications/ai-select-resume`, {
                candidateId: candidateId,
                jobId: job.id
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              if (aiRes.data && aiRes.data.success && aiRes.data.bestDocumentId) {
                documentId = aiRes.data.bestDocumentId;
              }
            } catch (aiErr) {
              console.error('AI selection failed:', aiErr.message);
            }
            
            if (!documentId) {
              documentId = currentCandidate.Document?.DocumentId || 
                               (currentCandidate.Documents || []).find(d => d.IsPrimary || d.IsPrimaryResume)?.DocumentId;
              
              if (!documentId) {
                try {
                  const docsRes = await axios.get(`${BASE_URL}/api/resumes/${candidateId}/documents`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  const primaryDoc = (docsRes.data || []).find(d => d.IsPrimary || d.IsPrimaryResume) || docsRes.data?.[0];
                  documentId = primaryDoc?.DocumentId || primaryDoc?.documentId;
                } catch (docErr) {
                  console.warn('Could not fetch documents list:', docErr.message);
                }
              }
            }

            if (documentId) {
              const downloadUrl = `${BASE_URL}/api/resumes/${documentId}/download`;
              const resumeRes = await axios.get(downloadUrl, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
              });
              
              if (resumeRes.data && resumeRes.data.size > 0) {
                const contentDisp = resumeRes.headers['content-disposition'];
                let fileName = 'resume.pdf';
                if (contentDisp && contentDisp.includes('filename=')) {
                  fileName = contentDisp.split('filename=')[1].replace(/[";]/g, '');
                }
                const file = new File([resumeRes.data], fileName, { type: resumeRes.data.type || 'application/pdf' });
                formData.append('resume', file);
              }
            }
          } catch (error) {
            console.error('Error fetching/attaching resume:', error.message);
          }
        }

        const response = await axios.post(`${BASE_URL}/api/recruitment/applications`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.data.success) {
          throw new Error(response.data.message || `Submission failed for ${cFirstName || 'Candidate'}`);
        }
      }

      Swal.close();
      await Swal.fire(
        'Submitted!',
        isBulk ? 'All profiles have been submitted successfully.' : 'Profile has been submitted successfully.',
        'success'
      );
      
      onClose();

      if (onBulkSuccess) {
        onBulkSuccess(targetCandidates);
      }
    } catch (error) {
      Swal.close();
      console.error('Error submitting profile:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to submit profiles.', 'error');
    } finally {
      setSubmittingJobId(null);
    }
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter(j => {
      // 1. Column-Scoped Text Search Filter
      const rawSearch = (searchTerm || '').trim().toLowerCase();
      const cleanSearch = rawSearch.replace(/^job-?/i, '');

      let matchesSearch = true;
      if (rawSearch) {
        switch (searchColumn) {
          case 'location': {
            const locStr = (j.location || '').toLowerCase();
            const ctryStr = (j.country || '').toLowerCase();
            const cityStr = (j.city || '').toLowerCase();
            const stateStr = (j.state || '').toLowerCase();

            const isExactWord = (target) => new RegExp(`(^|[^a-z0-9])${rawSearch}([^a-z0-9]|$)`, 'i').test(target);

            matchesSearch =
              isExactWord(locStr) ||
              isExactWord(ctryStr) ||
              isExactWord(cityStr) ||
              isExactWord(stateStr) ||
              ctryStr === rawSearch ||
              locStr.endsWith(',' + rawSearch) ||
              locStr.endsWith(', ' + rawSearch) ||
              locStr.startsWith(rawSearch + ',') ||
              locStr.startsWith(rawSearch + ' -') ||
              (rawSearch.length > 2 && (
                locStr.includes(rawSearch) ||
                ctryStr.includes(rawSearch) ||
                cityStr.includes(rawSearch) ||
                stateStr.includes(rawSearch)
              ));
            break;
          }
          case 'jobId': {
            matchesSearch =
              j.jobId?.toString().toLowerCase().includes(rawSearch) ||
              j.jobId?.toString().toLowerCase().includes(cleanSearch) ||
              j.systemId?.toString().toLowerCase().includes(rawSearch) ||
              j.systemId?.toString().toLowerCase().includes(cleanSearch) ||
              j.gbamsId?.toString().toLowerCase().includes(rawSearch) ||
              j.id?.toString().includes(rawSearch) ||
              j.id?.toString().includes(cleanSearch);
            break;
          }
          case 'title': {
            matchesSearch = j.title?.toLowerCase().includes(rawSearch);
            break;
          }
          case 'score': {
            if (rawSearch.startsWith('>=') || rawSearch.startsWith('>')) {
              const num = parseFloat(rawSearch.replace(/[>=]/g, ''));
              matchesSearch = !isNaN(num) ? j.score >= num : true;
            } else if (rawSearch.startsWith('<=') || rawSearch.startsWith('<')) {
              const num = parseFloat(rawSearch.replace(/[<=]/g, ''));
              matchesSearch = !isNaN(num) ? j.score <= num : true;
            } else {
              matchesSearch = j.score?.toString().includes(rawSearch);
            }
            break;
          }
          case 'workMode': {
            matchesSearch = j.workMode?.toLowerCase().includes(rawSearch);
            break;
          }
          case 'roleOwner': {
            matchesSearch = j.roleOwner?.toLowerCase().includes(rawSearch);
            break;
          }
          case 'assignedRecruiters': {
            matchesSearch = j.assignedRecruiters?.toLowerCase().includes(rawSearch);
            break;
          }
          case 'postedDate': {
            const dateStr = j.createdAt ? new Date(j.createdAt).toLocaleDateString() : '';
            const isoDate = j.createdAt ? new Date(j.createdAt).toISOString() : '';
            matchesSearch = dateStr.toLowerCase().includes(rawSearch) || isoDate.toLowerCase().includes(rawSearch);
            break;
          }
          case 'all':
          default: {
            matchesSearch =
              j.jobId?.toString().toLowerCase().includes(rawSearch) ||
              j.jobId?.toString().toLowerCase().includes(cleanSearch) ||
              j.systemId?.toString().toLowerCase().includes(rawSearch) ||
              j.systemId?.toString().toLowerCase().includes(cleanSearch) ||
              j.id?.toString().includes(rawSearch) ||
              j.id?.toString().includes(cleanSearch) ||
              j.gbamsId?.toString().toLowerCase().includes(rawSearch) ||
              j.title?.toLowerCase().includes(rawSearch) ||
              j.client?.toLowerCase().includes(rawSearch) ||
              j.location?.toLowerCase().includes(rawSearch) ||
              j.roleOwner?.toLowerCase().includes(rawSearch) ||
              j.assignedRecruiters?.toLowerCase().includes(rawSearch) ||
              j.workMode?.toLowerCase().includes(rawSearch) ||
              j.score?.toString().includes(rawSearch);
            break;
          }
        }
      }

      // 2. Match Mode Filter: 'matched' (Score > 0) vs 'all' (All Active Jobs)
      const matchesMatchFilter = matchFilter === 'all' || j.score > 0;

      // 3. Region / Country Filter
      const matchesRegion = isJobInRegion(j, regionFilter);

      // 4. Date Range Filter
      const matchesDate = isJobInDateRange(j, startDate, endDate);

      return matchesSearch && matchesMatchFilter && matchesRegion && matchesDate;
    });
  }, [jobs, searchTerm, searchColumn, matchFilter, regionFilter, startDate, endDate]);

  if (!isOpen) return null;

  return (
    <div className="si-modal-overlay" style={{ zIndex: 1000 }}>
      <div className="jobs-modal-container" style={{ maxWidth: '1280px', width: '95%' }}>
        
        <div className="si-modal-header" style={{ background: '#3b5bfd', borderBottom: 'none', padding: '18px 24px' }}>
          <h2 className="si-modal-title" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px' }}>
            <Sparkles size={20} fill="#fff" style={{ opacity: 0.9 }} /> Select Relevant Job for {candidates.length > 1 ? `${candidates.length} Candidates` : `"${candFullName}"`}
          </h2>
          <button 
            className="si-modal-close" 
            onClick={onClose}
            style={{ 
              background: 'rgba(255,255,255,0.15)', 
              color: 'white',
              border: 'none',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            <X size={18} />
          </button>
        </div>

        <div className="jobs-modal-content" style={{ padding: '20px 24px' }}>
          
          {/* ── Filter Toolbar ── */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#f8fafc',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            marginBottom: '16px'
          }}>
            {/* Column-Scoped Search Box */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              overflow: 'hidden',
              flex: '1 1 300px',
              maxWidth: '450px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
            }}>
              {/* Column Dropdown */}
              <select
                value={searchColumn}
                onChange={(e) => setSearchColumn(e.target.value)}
                style={{
                  padding: '7px 8px',
                  fontSize: '11.5px',
                  fontWeight: 700,
                  color: '#2563eb',
                  background: '#eff6ff',
                  border: 'none',
                  borderRight: '1px solid #cbd5e1',
                  cursor: 'pointer',
                  outline: 'none',
                  height: '33px',
                  maxWidth: '150px'
                }}
                title="Select column to search in"
              >
                <option value="all">🔍 All Columns</option>
                <option value="jobId">Job ID</option>
                <option value="title">Job Title</option>
                <option value="score">Score</option>
                <option value="location">Location</option>
                <option value="workMode">Work Mode</option>
                <option value="roleOwner">Role Owner</option>
                <option value="assignedRecruiters">Assigned Recruiters</option>
                <option value="postedDate">Posted Date</option>
              </select>

              {/* Search Input Field */}
              <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                <input 
                  type="text" 
                  placeholder={getSearchPlaceholder(searchColumn)} 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '7px 26px 7px 10px',
                    fontSize: '12px',
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    color: '#1e293b'
                  }}
                />
                {searchTerm && (
                  <X 
                    size={13} 
                    style={{ position: 'absolute', right: '8px', cursor: 'pointer', color: '#94a3b8' }} 
                    onClick={() => setSearchTerm('')} 
                    title="Clear search"
                  />
                )}
              </div>
            </div>

            {/* Match Mode Dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Sparkles size={14} style={{ color: '#3b5bfd' }} />
              <select
                value={matchFilter}
                onChange={(e) => setMatchFilter(e.target.value)}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#1e293b',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="matched">✨ Matched Only</option>
                <option value="all">📋 All Active Jobs</option>
              </select>
            </div>

            {/* Region Filter Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#e2e8f0', padding: '3px', borderRadius: '8px' }}>
              <button
                type="button"
                onClick={() => setRegionFilter('all')}
                style={{
                  padding: '5px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: regionFilter === 'all' ? '#3b5bfd' : 'transparent',
                  color: regionFilter === 'all' ? '#ffffff' : '#475569',
                  boxShadow: regionFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                All Regions
              </button>
              <button
                type="button"
                onClick={() => setRegionFilter('onsite')}
                style={{
                  padding: '5px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: regionFilter === 'onsite' ? '#3b5bfd' : 'transparent',
                  color: regionFilter === 'onsite' ? '#ffffff' : '#475569',
                  boxShadow: regionFilter === 'onsite' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                Onsite
              </button>
              <button
                type="button"
                onClick={() => setRegionFilter('offshore')}
                style={{
                  padding: '5px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: regionFilter === 'offshore' ? '#3b5bfd' : 'transparent',
                  color: regionFilter === 'offshore' ? '#ffffff' : '#475569',
                  boxShadow: regionFilter === 'offshore' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                Offshore
              </button>
            </div>

            {/* Date Quick Select & Range */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Calendar size={14} style={{ color: '#64748b' }} />
                <select
                  value={dateQuickSelect}
                  onChange={(e) => handleQuickDateChange(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#334155',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="this_week">This Week</option>
                  <option value="last_week">Last Week</option>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {(dateQuickSelect === 'custom' || startDate || endDate) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input 
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setDateQuickSelect('custom'); }}
                    style={{ padding: '4px 6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b' }}>to</span>
                  <input 
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setDateQuickSelect('custom'); }}
                    style={{ padding: '4px 6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  />
                </div>
              )}

              {/* Reset Filters Button */}
              {(searchTerm || searchColumn !== 'all' || matchFilter !== 'matched' || regionFilter !== 'all' || dateQuickSelect !== 'all') && (
                <button
                  type="button"
                  title="Reset All Filters"
                  onClick={() => {
                    setSearchTerm('');
                    setSearchColumn('all');
                    setMatchFilter('matched');
                    setRegionFilter('all');
                    setDateQuickSelect('all');
                    setStartDate('');
                    setEndDate('');
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #fecaca',
                    background: '#fff5f5',
                    color: '#dc2626',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <RotateCcw size={12} /> Reset
                </button>
              )}

              <button
                type="button"
                title="Refresh Active Jobs from Server"
                onClick={() => fetchJobs(true)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <RotateCcw size={12} /> Refresh Jobs
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              Showing <strong>{filteredJobs.length}</strong> of <strong>{jobs.length}</strong> {matchFilter === 'matched' ? 'matched' : 'active'} jobs for <strong>{candidates.length > 1 ? `${candidates.length} Candidates` : `${candFullName}'s profile`}</strong>.
            </p>
            {(matchFilter !== 'matched' || regionFilter !== 'all' || dateQuickSelect !== 'all') && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {matchFilter === 'all' && (
                  <span style={{ fontSize: '11px', background: '#f1f5f9', color: '#334155', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                    📋 All Active Jobs
                  </span>
                )}
                {regionFilter !== 'all' && (
                  <span style={{ fontSize: '11px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                    📍 {regionFilter === 'onsite' ? 'Onsite' : 'Offshore'}
                  </span>
                )}
                {dateQuickSelect !== 'all' && (
                  <span style={{ fontSize: '11px', background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                    📅 {dateQuickSelect.replace('_', ' ').toUpperCase()} {startDate && endDate ? `(${startDate} to ${endDate})` : ''}
                  </span>
                )}
              </div>
            )}
          </div>

          <div style={{ overflowX: 'auto', maxHeight: '55vh', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>JOB ID</th>
                  <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>JOB TITLE (RELEVANCE)</th>
                  <th style={{ textAlign: 'center', padding: '14px 12px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>SCORE</th>
                  <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>LOCATION</th>
                  <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>WORK MODE</th>
                  <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>ROLE OWNER</th>
                  <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>ASSIGNED RECRUITERS</th>
                  <th style={{ textAlign: 'left', padding: '14px 12px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>POSTED DATE</th>
                  <th style={{ textAlign: 'center', padding: '14px 12px', fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.5px' }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '60px' }}>
                      <Loader className="spin" size={32} color="#3b5bfd" style={{ margin: '0 auto', animation: 'spin 1s linear infinite' }} />
                      <p style={{ marginTop: '12px', color: '#64748b' }}>Analyzing jobs for matching profile...</p>
                    </td>
                  </tr>
                ) : filteredJobs.length > 0 ? filteredJobs.map((job, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ fontWeight: 700, color: '#3b5bfd', padding: '16px 12px', fontSize: '13px' }}>{job.jobId}</td>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>{job.title}</span>
                        {job.score > 0 && (
                          <span style={{ 
                            fontSize: '9px', 
                            background: '#dcfce7', 
                            color: '#166534', 
                            padding: '1px 6px', 
                            borderRadius: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            width: 'fit-content',
                            fontWeight: 700,
                            textTransform: 'uppercase'
                          }}>
                            <Sparkles size={8} fill="#166534" /> Relevant
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      {job.score > 0 ? (
                        <span style={{ 
                          fontWeight: 700, 
                          color: '#3b5bfd', 
                          background: '#eff6ff', 
                          padding: '4px 10px', 
                          borderRadius: '6px', 
                          fontSize: '13px'
                        }}>
                          {job.score}
                        </span>
                      ) : (
                        <span style={{ 
                          fontWeight: 600, 
                          color: '#94a3b8', 
                          background: '#f8fafc', 
                          padding: '4px 10px', 
                          borderRadius: '6px', 
                          fontSize: '12px'
                        }}>
                          —
                        </span>
                      )}
                    </td>
                    <td title={job.location || ''} style={{ padding: '16px 12px', color: '#475569', fontSize: '12px' }}>
                      {(job.location || '').length > 25 ? (job.location || '').substring(0, 25) + '...' : (job.location || 'N/A')}
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      <span style={{ 
                        fontSize: '11px', 
                        background: job.workMode === 'Remote' ? '#eff6ff' : '#fff7ed', 
                        color: job.workMode === 'Remote' ? '#3b5bfd' : '#c2410c', 
                        padding: '2px 8px', 
                        borderRadius: '4px',
                        fontWeight: 600
                      }}>
                        {job.workMode || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '16px 12px', color: '#475569', fontSize: '12px' }}>{job.roleOwner || 'N/A'}</td>
                    <td title={job.assignedRecruiters || ''} style={{ padding: '16px 12px', color: '#475569', fontSize: '12px' }}>
                      {(job.assignedRecruiters || '').length > 25 ? (job.assignedRecruiters || '').substring(0, 25) + '...' : (job.assignedRecruiters || 'N/A')}
                    </td>
                    <td style={{ padding: '16px 12px', color: '#475569', fontSize: '12px' }}>
                      {job.createdAt ? new Date(job.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <button 
                        className="si-btn-save" 
                        style={{ 
                          background: '#3b5bfd', 
                          padding: '8px 12px', 
                          fontSize: '12px', 
                          borderRadius: '6px', 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          margin: '0 auto',
                          height: '36px',
                          border: 'none',
                          color: 'white',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#3b5bfd'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        onClick={() => handleSubmitProfile(job)}
                        disabled={submittingJobId !== null}
                      >
                        <Send size={14} /> {submittingJobId === job.id ? 'Submitting...' : 'Submit Profile'}
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                      {searchTerm ? 'No matching jobs found' : 'No relevant jobs found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>

        <div className="si-modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', justifyContent: 'center' }}>
          <button 
            className="si-btn-cancel" 
            onClick={onClose} 
            disabled={submittingJobId !== null}
            style={{ 
              padding: '10px 40px', 
              borderRadius: '8px', 
              background: '#f1f5f9', 
              border: '1px solid #e2e8f0',
              color: '#475569',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#1e293b'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
          >
            Close
          </button>
        </div>


      </div>
    </div>
  );
};
